import * as React from 'react';
import { Alert, CircularProgress, Typography } from '@mui/material';

import { ClosableModal, PrimaryOutlinedButton } from 'components';
import { ICertificate } from '../../../../interface';
import { getCertificateTemplateMeta } from './template-catalog';
import {
  buildCertificateRenderRequest,
  getCertificatePreviewFrameHeight,
  renderCertificatePreviewHtmlViaService,
} from './utils';
import './styles.scss';

interface Props {
  open: boolean;
  onClose: () => void;
  certificate: ICertificate;
  sessionTitle?: string;
  moduleTitles?: string[];
  enterprise?: {
    entLogo?: string | null;
    logo?: string | null;
    name?: string | null;
  } | null;
}

const DEFAULT_SESSION_TITLE = 'Session Title';
const PRINT_FRAME_ID = 'certificate-preview-print-frame';

const buildPrintableHtml = (html: string) => html.replace(
  '</head>',
  `<style media="print">
    @page {
      size: A4 landscape;
      margin: 0;
    }

    html,
    body {
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .certificate-page {
      overflow: hidden !important;
    }

    .certificate-shell {
      transform: scale(0.9);
      transform-origin: top left;
    }

    .certificate-page:nth-of-type(n + 3) {
      display: none !important;
    }
  </style>
  </head>`,
);

const CertificatePreviewModal = ({
  open,
  onClose,
  certificate,
  sessionTitle = DEFAULT_SESSION_TITLE,
  moduleTitles = [],
  enterprise = null,
}: Props) => {
  const templateMeta = getCertificateTemplateMeta(certificate.template);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [previewHtml, setPreviewHtml] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = React.useState(false);

  const renderRequest = React.useMemo(
    () => buildCertificateRenderRequest({
      certificate,
      enterprise,
      sessionTitle,
      moduleTitles,
    }),
    [certificate, enterprise, moduleTitles, sessionTitle],
  );
  React.useEffect(() => {
    let ignore = false;

    const loadPreview = async () => {
      if (!open) {
        if (!ignore) {
          setPreviewHtml('');
          setError(null);
          setIsLoadingPreview(false);
        }
        return;
      }

      setIsLoadingPreview(true);
      setError(null);

      try {
        const html = await renderCertificatePreviewHtmlViaService(renderRequest);
        if (!ignore) {
          setPreviewHtml(html);
        }
      } catch (loadError) {
        if (!ignore) {
          setPreviewHtml('');
          setError(
            loadError instanceof Error ? loadError.message : 'Failed to load certificate preview',
          );
        }
      } finally {
        if (!ignore) {
          setIsLoadingPreview(false);
        }
      }
    };

    loadPreview();
    return () => {
      ignore = true;
    };
  }, [open, renderRequest]);

  const handleDownloadPdf = () => {
    if (!previewHtml) return;

    setIsDownloading(true);

    const previousFrame = document.getElementById(PRINT_FRAME_ID);
    previousFrame?.remove();

    const printFrame = document.createElement('iframe');
    printFrame.id = PRINT_FRAME_ID;
    printFrame.setAttribute('aria-hidden', 'true');

    // keep it offscreen but renderable
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.style.opacity = '0';

    document.body.appendChild(printFrame);

    let cleanedUp = false;

    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;

      setTimeout(() => {
        printFrame.remove();
        setIsDownloading(false);
      }, 3000);
    };

    printFrame.onload = () => {
      const frameWindow = printFrame.contentWindow;

      if (!frameWindow) {
        cleanup();
        return;
      }

      frameWindow.onafterprint = cleanup;

      setTimeout(() => {
        try {
          frameWindow.focus();
          frameWindow.print();
        } catch (_error) {
          cleanup();
        }
      }, 300);
    };

    printFrame.srcdoc = buildPrintableHtml(previewHtml);

    setTimeout(() => {
      cleanup();
    }, 5000);
  };

  const frameHeight = getCertificatePreviewFrameHeight(certificate.template);
  let previewBody: React.ReactNode = null;

  if (isLoadingPreview) {
    previewBody = (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
        <CircularProgress />
      </div>
    );
  } else if (previewHtml) {
    previewBody = (
      <iframe
        title="Certificate preview"
        srcDoc={previewHtml}
        style={{
          width: '100%',
          height: `${frameHeight}px`,
          border: '1px solid rgba(15, 23, 42, 0.12)',
          borderRadius: '16px',
          background: '#fff',
        }}
      />
    );
  }

  return (
    <ClosableModal
      open={open}
      onClose={onClose}
      title={`${templateMeta.shortLabel} preview`}
      subTitle="Preview your generated certificate before proceeding."
      modalClass="certificate-preview-modal"
    >
      <div className="certificate-preview-modal__content">
        <div className="certificate-preview-modal__toolbar">
          <div>
            <Typography variant="body2" color="text.secondary">
              {templateMeta.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {certificate.title || 'Certificate of Completion'}
            </Typography>
          </div>
          <PrimaryOutlinedButton
            type="button"
            onClick={handleDownloadPdf}
            disabled={isLoadingPreview || isDownloading || !previewHtml}
          >
            {isDownloading ? 'Preparing PDF...' : 'Download PDF'}
          </PrimaryOutlinedButton>
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {previewBody}
      </div>
    </ClosableModal>
  );
};

CertificatePreviewModal.defaultProps = {
  sessionTitle: DEFAULT_SESSION_TITLE,
  moduleTitles: [],
  enterprise: null,
};

export default CertificatePreviewModal;
